import { ActionType } from 'typesafe-actions';

import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';

import * as actions from '../actions/knmActions';
import { KnmActionsEnum } from '../enums/KnmActionsEnum';
import { KnmStatusesEnum } from '../enums/KnmStatusesEnum';
import { IKnmState } from '../types/IKnmState';
import { IEpguNotification } from '../../../APIPackage/KnmModule/interfaces/IEpguNotification';

export type KnmAction = ActionType<typeof actions>;

const initialState: IKnmState = {
  isOrgInSonkoRegistry: {},
  epguNotificationList: {},
  epguNotificationTotalCount: {},
  ervkObjectsOfControl: [],
  npaData: [],
  punishmentNature: [],
  isNeedActSaveOrSign: undefined,
  collaboratingKnoOrganizations: {
    knoOrganisations: [],
    organisationsPageCount: 0,
    organisationsTotalCount: 0,
    organisationsPage: '',
  },
  knmLoading: true,
  lateChangesFieldLinks: new Map<string, string>(),
  externalParams: {},
};

export function knmReducer(state: IKnmState = initialState, action: KnmAction): IKnmState {
  switch (action.type) {
    case KnmActionsEnum.KNM_RESET: {
      return initialState;
    }
    case KnmActionsEnum.KNM_SET_ACTIONS_DROPDOWN_VISIBLE: {
      return {
        ...state,
        actionsDropdownVisible: action.payload.visible,
      };
    }
    case KnmActionsEnum.KNM_SET_URL_ID: {
      return {
        ...state,
        urlId: action.payload.urlId,
      };
    }
    case KnmActionsEnum.KNM_SET_RAW_DATA: {
      return {
        ...state,
        rawData: action.payload.rawData,
      };
    }
    case KnmActionsEnum.KNM_PATCH_RAW_DATA: {
      const { partRawData } = action.payload;

      return {
        ...state,
        rawData: state.rawData
          ? {
              ...state.rawData,
              ...partRawData,
            }
          : (partRawData as IKnm),
      };
    }
    case KnmActionsEnum.KNM_ADD_VISIBLE_MODALS: {
      return {
        ...state,
        knmVisibleModals: state?.knmVisibleModals
          ? [...state.knmVisibleModals, action.payload.value]
          : [action.payload.value],
        modalParams: action.payload.params,
      };
    }
    case KnmActionsEnum.KNM_REMOVE_VISIBLE_MODALS: {
      return {
        ...state,
        knmVisibleModals: state?.knmVisibleModals?.filter(item => item !== action.payload.value),
      };
    }
    case KnmActionsEnum.KNM_GET_REQUEST: {
      return {
        ...state,
        getKnm: {
          ...state?.getKnm,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_GET_SUCCESS: {
      return {
        ...state,
        rawData: action.payload.data,
        getKnm: {
          ...state?.getKnm,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_GET_FAILURE: {
      return {
        ...state,
        getKnm: {
          ...state?.getKnm,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_UPDATE_REQUEST: {
      return {
        ...state,
        updateKnm: {
          ...state?.updateKnm,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_UPDATE_SUCCESS: {
      return {
        ...state,
        isChangePlanReasonActive: false,
        rawData: action.payload.data,
        updateKnm: {
          ...state?.updateKnm,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_UPDATE_FAILURE: {
      return {
        ...state,
        updateKnm: {
          ...state?.updateKnm,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_COPY_REQUEST: {
      return {
        ...state,
        copyKnm: {
          ...state?.copyKnm,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_COPY_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        rawData: data,
        urlId: data.id.toString(),
        copyKnm: {
          ...state?.copyKnm,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_COPY_FAILURE: {
      return {
        ...state,
        copyKnm: {
          ...state?.copyKnm,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN_REQUEST: {
      return {
        ...state,
        excludeKnmFromPlan: {
          ...state?.excludeKnmFromPlan,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN_SUCCESS: {
      return {
        ...state,
        excludeKnmFromPlan: {
          ...state?.excludeKnmFromPlan,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN_FAILURE: {
      return {
        ...state,
        excludeKnmFromPlan: {
          ...state?.excludeKnmFromPlan,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERKNM_KNO_ORGANIZATION_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          erknmKnoOrganization: action.payload.knoOrganization,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERKNM_SUPERVISION_TYPE_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          erknmSupervisionType: action.payload.supervisionType,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERKNM_KNM_KIND_VERSION_ID_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          erknmKnmKindVersionId: action.payload.knmKindVersionId,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERROR_FIELDS: {
      return {
        ...state,
        errorFields: action.payload.errorFields,
      };
    }
    case KnmActionsEnum.KNM_SET_STATUS_HINT_TEXT: {
      return {
        ...state,
        statusHintText: action.payload.value,
      };
    }
    case KnmActionsEnum.KNM_SET_STATUS_HINT_FIELDS: {
      return {
        ...state,
        statusHintFields: action.payload.fields,
      };
    }
    case KnmActionsEnum.KNM_SET_FORM_REF: {
      return {
        ...state,
        formRef: action.payload.formRef,
      };
    }
    case KnmActionsEnum.KNM_SET_KNO_ORGANIZATION_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          knoOrganization: action.payload.knoOrganization,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_KNO_ORGANIZATION_ERVK_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          knoOrganizationErvk: action.payload.knoOrganizationErvk,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_FEDERAL_LAW_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          federalLaw: action.payload.federalLaw,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_LEGAL_ENTITY_DATA: {
      return {
        ...state,
        legalEntityData: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_LOADING_FLAGS: {
      const { name, value: isLoading } = action.payload;

      return {
        ...state,
        loadingFlags: {
          ...state?.loadingFlags,
          [name]: isLoading,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_PATH: {
      return {
        ...state,
        path: action.payload.path,
      };
    }
    case KnmActionsEnum.KNM_SET_PUBLISHED_STATUS: {
      return {
        ...state,
        publishedStatus: action.payload.publishedStatus,
      };
    }
    case KnmActionsEnum.KNM_SET_EDIT_STATUS: {
      return {
        ...state,
        editStatus: action.payload.status,
      };
    }
    case KnmActionsEnum.KNM_SET_EDITOR_TIME: {
      return {
        ...state,
        editorTime: action.payload.time,
      };
    }
    case KnmActionsEnum.KNM_SET_PREV_KNM_EDITORS: {
      return {
        ...state,
        prevKnmEditors: action.payload.knmEditors,
      };
    }
    case KnmActionsEnum.KNM_SET_STATUS: {
      return {
        ...state,
        status: action.payload.status,
      };
    }
    case KnmActionsEnum.KNM_SET_STATUS_TEXT: {
      return {
        ...state,
        statusText: action.payload.statusText,
      };
    }
    case KnmActionsEnum.KNM_SET_SUBJECT_TYPE_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          subjectType: action.payload.subjectType,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_SUPERVISION_TYPE_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          supervisionType: action.payload.supervisionType,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_SUPERVISION_TYPE_ERVK_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          supervisionTypeErvk: action.payload.supervisionTypeErvk,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_TYPE_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          type: action.payload.type,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_TYPE_ERVK_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          typeErvk: action.payload.type,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_SYSTEM_TYPE: {
      return {
        ...state,
        systemType: action.payload.systemType,
      };
    }
    case KnmActionsEnum.KNM_SET_IS_CHANGE_PLAN_REASON_ACTIVE: {
      return {
        ...state,
        isChangePlanReasonActive: action.payload.isChangePlanReasonActive,
      };
    }
    case KnmActionsEnum.KNM_SET_ON_SAVE_BUTTON_CLICK: {
      return {
        ...state,
        onSaveButtonClick: action.payload.onSaveButtonClick,
      };
    }
    case KnmActionsEnum.KNM_SET_ON_ADMIN_SAVE_BUTTON_CLICK: {
      return {
        ...state,
        onAdminSaveButtonClick: action.payload.onAdminSaveButtonClick,
      };
    }
    case KnmActionsEnum.KNM_SET_OPEN_CHANGE_KNM_MODAL: {
      return {
        ...state,
        openChangeKnmModal: action.payload.openChangeKnmModal,
      };
    }
    case KnmActionsEnum.KNM_SET_OPEN_REMOVE_FROM_PLAN_MODAL: {
      return {
        ...state,
        openRemoveFromPlanModal: action.payload.openRemoveFromPlanModal,
      };
    }
    case KnmActionsEnum.KNM_SET_PUBLISH_KNM: {
      return {
        ...state,
        publishKnm: action.payload.publishKnm,
      };
    }
    case KnmActionsEnum.KNM_SET_KNM_END_BY_ERKNM: {
      return {
        ...state,
        knmEndByErknm: action.payload.knmEndByErknm,
      };
    }
    case KnmActionsEnum.KNM_SET_UNPUBLISH_KNM: {
      return {
        ...state,
        unpublishKnm: action.payload.unpublishKnm,
      };
    }
    case KnmActionsEnum.KNM_SET_DOWNLOAD_PROSECUTOR_DOCS: {
      return {
        ...state,
        downloadProsecutorDocs: action.payload.downloadProsecutorDocs,
      };
    }
    case KnmActionsEnum.KNM_PLAN_GET_NPA_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        npaData: data,
      };
    }
    case KnmActionsEnum.KNM_PLAN_GET_PUNISHMENT_NATURE_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        punishmentNature: data,
      };
    }
    case KnmActionsEnum.KNM_PLAN_FIND_GET_REQUEST: {
      return {
        ...state,
        planFind: {
          ...state?.planFind,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_PLAN_FIND_GET_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        planFind: {
          ...state?.planFind,
          data,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_PLAN_FIND_GET_FAILURE: {
      return {
        ...state,
        planFind: {
          ...state?.planFind,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_CLEAR: {
      return {
        ...state,
        validatedUnsignedKnmByPlan: undefined,
      };
    }
    case KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_REQUEST: {
      return {
        ...state,
        validatedUnsignedKnmByPlan: {
          ...state?.validatedUnsignedKnmByPlan,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        validatedUnsignedKnmByPlan: {
          ...data,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_FAILURE: {
      return {
        ...state,
        validatedUnsignedKnmByPlan: {
          ...state?.validatedUnsignedKnmByPlan,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_VIOLATIONS_GET_REQUEST: {
      return {
        ...state,
        violations: {
          ...state?.violations,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_VIOLATIONS_GET_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        violations: {
          ...state?.violations,
          data,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_VIOLATIONS_GET_FAILURE: {
      return {
        ...state,
        violations: {
          ...state?.violations,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_HISTORY_CHANGES_IS_ON: {
      return {
        ...state,
        knmHistoryChangesIsOn: action.payload.value,
      };
    }
    case KnmActionsEnum.KNM_SET_HISTORY_CHANGES_IS_LOADING: {
      return {
        ...state,
        knmHistoryChangesIsLoading: action.payload.value,
      };
    }
    case KnmActionsEnum.KNM_SET_HISTORY_CHANGES: {
      return {
        ...state,
        knmHistoryChanges: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_SIGNED_HISTORY_CHANGES: {
      return {
        ...state,
        knmSignedHistoryChanges: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_COMPLETE336_SUCCESS: {
      return {
        ...state,
        rawData: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_SUBJECT_DATA: {
      const { subjectData } = action.payload;

      return {
        ...state,
        subjectData,
      };
    }
    case KnmActionsEnum.KNM_SET_AVAILABILITY_OF_CHANGING_SUBJECT_TYPE: {
      const { isAvailable } = action.payload;

      return {
        ...state,
        isChangeSubjectTypeFromActionsAvailable: isAvailable,
      };
    }
    case KnmActionsEnum.KNM_INFO_SET_EXCLUDE_DATES: {
      return {
        ...state,
        excludeDates: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_INFO_SET_WORK_DAYS_COUNT_BEFORE_START: {
      return {
        ...state,
        workDaysCountBeforeStart: action.payload.count,
      };
    }
    case KnmActionsEnum.KNM_SET_PLANNED_ACTIVITIES: {
      return {
        ...state,
        plannedActivities: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_NKO_FOREIGN_AGENT_DATA_BY_OGRN: {
      return {
        ...state,
        nkoForeignAgentData: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_IS_ORG_IN_SONKO_REGISTRY: {
      const { ogrn, inn, isPresent } = action.payload;
      const key = `${ogrn || ''}|${inn || ''}`;

      return {
        ...state,
        isOrgInSonkoRegistry: {
          ...state.isOrgInSonkoRegistry,
          [key]: isPresent,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ACCESS_SETTINGS_FORM_REF: {
      return {
        ...state,
        accessSettingsFormRef: action.payload.formRef,
      };
    }
    case KnmActionsEnum.KNM_SET_INITIAL_ACCESS_SETTINGS_FORM_VALUES: {
      const { data } = action.payload;

      return {
        ...state,
        initialAccessSettingsFormValues: {
          ...state?.initialAccessSettingsFormValues,
          ...data,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ACCESS_NOTICE_ERP_ID: {
      return {
        ...state,
        accessNoticeErpId: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_ACCESS_NOTICE_KNO_ORGANIZATION_NAME: {
      return {
        ...state,
        accessNoticeKnoOrganizationName: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_CREATED_JUDGMENT_COURT: {
      return {
        ...state,
        createdCourt: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_ADD_EPGU_NOTIFICATION_LIST: {
      const { organizationGuid, data } = action.payload;
      const oldData: IEpguNotification[] = state.epguNotificationList[organizationGuid] || [];

      return {
        ...state,
        epguNotificationList: {
          ...state.epguNotificationList,
          [organizationGuid]: [...oldData, ...data],
        },
      };
    }
    case KnmActionsEnum.KNM_SET_EPGU_NOTIFICATION_TOTAL_COUNT: {
      const { organizationGuid, count } = action.payload;

      return {
        ...state,
        epguNotificationTotalCount: {
          ...state.epguNotificationTotalCount,
          [organizationGuid]: count,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_APPROVE_ADD_NOTIFICATION_FUNCTION: {
      return {
        ...state,
        approveAddNotificationFunction: action.payload.approveFunction,
      };
    }
    case KnmActionsEnum.KNM_ADD_ERVK_OBJECTS_OF_CONTROL: {
      return {
        ...state,
        ervkObjectsOfControl: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_ERKNM_REASONS_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          reasonsErknm: action.payload.reasons,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERP_REASONS_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          reasons: action.payload.reasons,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERP_REASONS_ERVK_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          reasonsErvk: action.payload.reasons,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ERP_KIND_ERVK_VERSION_ID_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          kindErvk: action.payload.kindErvk,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_PM_IS_UNABLE_TO_REFUSE_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          isUnableToRefuse: action.payload.isUnableToRefuse,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_ORDER_VERSION_ID: {
      return {
        ...state,
        orderVersionId: action.payload.orderVersionId,
      };
    }
    case KnmActionsEnum.KNM_SET_ORDERS: {
      return {
        ...state,
        knmOrdersSelectOptions: action.payload.knmOrders,
        knmOrderConditionId: action.payload.conditionId,
      };
    }
    case KnmActionsEnum.KNM_PLAN_EXCLUSION_REASONS_SET: {
      return {
        ...state,
        planExclusionReasonsSelectOptions: action.payload.options,
      };
    }
    case KnmActionsEnum.KNM_PLAN_EXCLUSION_REASONS_RESET: {
      return {
        ...state,
        planExclusionReasonsSelectOptions: undefined,
      };
    }
    case KnmActionsEnum.KNM_CLEAR_ERVK_OBJECTS_OF_CONTROL: {
      return {
        ...state,
        ervkObjectsOfControl: [],
      };
    }

    case KnmActionsEnum.KNM_CHECK_CHANGES_REQUEST: {
      return {
        ...state,
        checkChanges: {
          ...state?.checkChanges,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_CHECK_CHANGES_SUCCESS: {
      return {
        ...state,
        checkChanges: {
          ...state?.checkChanges,
          isChanges: action.payload.data,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_CHECK_CHANGES_FAILURE: {
      return {
        ...state,
        checkChanges: {
          ...state?.checkChanges,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_CHANGE_PLAN_REASON_AFTER_CONFIRM: {
      return {
        ...state,
        changePlanReasonAfterConfirm: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_IS_CAN_BE_SIGNED: {
      return {
        ...state,
        isKnmCanBeSigned: action.payload.data,
      };
    }
    case KnmActionsEnum.KNM_SET_INVALID_FIELDS: {
      return {
        ...state,
        invalidFields: action.payload.fields,
      };
    }
    case KnmActionsEnum.KNM_CLEAR_INVALID_FIELDS: {
      return {
        ...state,
        invalidFields: undefined,
      };
    }
    case KnmActionsEnum.KNM_SET_CHECK_KNO_SUPERVISION_LINKS: {
      return {
        ...state,
        ervkCorrectionRequired: action.payload.data,
      };
    }

    case KnmActionsEnum.KNM_ERVK_PROSECUTOR_REQUEST: {
      return {
        ...state,
        ervkProsecutorOrganization: {
          ...state?.ervkProsecutorOrganization,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case KnmActionsEnum.KNM_ERVK_PROSECUTOR_SUCCESS: {
      return {
        ...state,
        ervkProsecutorOrganization: {
          data: action.payload.data,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case KnmActionsEnum.KNM_ERVK_PROSECUTOR_FAILURE: {
      return {
        ...state,
        checkChanges: {
          ...state?.ervkProsecutorOrganization,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }

    case KnmActionsEnum.KNM_SET_KNO_COLLABORATING_ORGANISATIONS: {
      const { knoOrganisations, organisationsPage, organisationsPageCount, organisationsTotalCount } = action.payload;

      return {
        ...state,
        collaboratingKnoOrganizations: {
          knoOrganisations,
          organisationsPage,
          organisationsPageCount,
          organisationsTotalCount,
        },
      };
    }
    case KnmActionsEnum.KNM_SET_IS_SHOW_APPROVE_REMARKS: {
      return {
        ...state,
        isShowApproveRemarks: action.payload.isShow,
      };
    }
    case KnmActionsEnum.KNM_SET_LOADING: {
      return {
        ...state,
        knmLoading: action.payload.loading,
      };
    }
    case KnmActionsEnum.KNM_GET_LATE_CHANGES_FIELDS_REQUEST: {
      return {
        ...state,
      };
    }
    case KnmActionsEnum.KNM_GET_LATE_CHANGES_FIELDS_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        lateChangesFields: data,
      };
    }
    case KnmActionsEnum.KNM_GET_LATE_CHANGES_FIELDS_FAILURE: {
      return {
        ...state,
      };
    }
    case KnmActionsEnum.KNM_SET_LATE_CHANGES_FIELDS_LINKS: {
      const { data } = action.payload;

      return {
        ...state,
        lateChangesFieldLinks: data,
      };
    }
    case KnmActionsEnum.KNM_SET_IS_NEED_ACT_SAVE_OR_SIGN: {
      return {
        ...state,
        isNeedActSaveOrSign: action.payload.value,
      };
    }
    case KnmActionsEnum.KNM_SET_PRESCRIBE_DATA: {
      const { prescribeData } = action.payload;

      return {
        ...state,
        prescribeDataForSign: prescribeData,
      };
    }
    case KnmActionsEnum.KNM_SET_PRESCRIBE_SIGNED: {
      const { isSignComplete } = action.payload;

      return {
        ...state,
        isCancelSignCompleted: isSignComplete,
      };
    }
    case KnmActionsEnum.KNM_SET_EXTERNAL_PARAMS: {
      return {
        ...state,
        externalParams: action.payload.value,
      };
    }
    case KnmActionsEnum.KNM_SET_IS_SAVE_ON_PROCESS: {
      return {
        ...state,
        isSaveOnProcess: action.payload.value,
      };
    }

    default:
      return state;
  }
}
